import { createAsyncThunk } from '@reduxjs/toolkit';
import type { User, Role } from '@/types';
import api from '@/api/client';
import { setUsers, removeUser, editUser } from '../slices/userSlice';
import { setToast } from '../slices/toastSlice';

export const fetchUsers = createAsyncThunk('user/fetch', async (_, { dispatch, rejectWithValue }) => {
  try {
    const { data } = await api.get<{ data: User[] }>('/users/');
    dispatch(setUsers(data.data ?? []));
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Erreur chargement utilisateurs';
    dispatch(setToast({ message, severity: 'error' }));
    return rejectWithValue(message);
  }
});

export const createUser = createAsyncThunk<boolean, Partial<User>, { rejectValue: string }>(
  'user/create',
  async (user, { dispatch, rejectWithValue }) => {
    try {
      await api.post('/users/', user);
      await dispatch(fetchUsers());
      dispatch(setToast({ message: 'Utilisateur créé', severity: 'success' }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur création';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const updateUser = createAsyncThunk<boolean, User, { rejectValue: string }>(
  'user/update',
  async (user, { dispatch, rejectWithValue }) => {
    try {
      await api.put(`/users/${user.id}`, user);
      dispatch(editUser(user));
      dispatch(setToast({ message: 'Utilisateur modifié', severity: 'success' }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur modification';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const deleteUser = createAsyncThunk<boolean, number, { rejectValue: string }>(
  'user/delete',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`/users/${id}`);
      dispatch(removeUser(id));
      dispatch(setToast({ message: 'Utilisateur supprimé', severity: 'success' }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur suppression';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const updateUserPassword = createAsyncThunk<boolean, string, { rejectValue: string }>(
  'user/updatePassword',
  async (password, { dispatch, rejectWithValue }) => {
    try {
      await api.put(`/users/update-pwd?pwd=${encodeURIComponent(password)}`);
      dispatch(setToast({ message: 'Mot de passe modifié', severity: 'success' }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur modification';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const grantRolesToUser = createAsyncThunk<
  void,
  { userId: number; roles: Role[] },
  { rejectValue: string }
>('user/grantRoles', async ({ userId, roles }, { dispatch, rejectWithValue }) => {
  try {
    await api.put(`/users/grant-roles/${userId}`, { roles });
    await dispatch(fetchUsers());
    dispatch(setToast({ message: 'Rôles attribués', severity: 'success' }));
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Erreur attribution rôles';
    dispatch(setToast({ message, severity: 'error' }));
    return rejectWithValue(message);
  }
});
